// A fight seen from outside: while a party is away in its arena, everyone left on the field sees the party's
// leader and the creature scrapping in a cartoon dust cloud where the fight started. When it ends, the winner
// comes out of the cloud: the creature pops (a win), or the party poofs away to their wayshrine (a wipe).
import ink from '../ink/ink.js';
import { drawMob } from './inkmobs.js';
import { People, drawPerson } from './inkchar.js';
import { placeTag } from './inkui.js';
import { mobDef } from '../../../shared/mobs.js';
import { sanitizeLook } from '../../../shared/look.js';
import type { CircleView } from '../../../shared/battle.js';

const I = ink as any;
const PAPER = '#fbf6ea';

/** When each finished fight was first seen finished (so its ending plays from the start). */
const endedAt = new Map<number, number>();
export function forgetBrawls(ids: Set<number>): void { for (const k of endedAt.keys()) if (!ids.has(k)) endedAt.delete(k); }

/** Draw one fight at its circle. `k` scales it (smaller on the world map). Feet at the circle's middle. */
export function drawBrawl(g: CanvasRenderingContext2D, c: CircleView, people: People, now: number, dt: number, k = 1): void {
  if (!c.lead || !c.foe) return;
  if (c.done && !endedAt.has(c.id)) endedAt.set(c.id, now);
  const end = c.done ? (now - endedAt.get(c.id)!) / 1000 : -1, t = now / 1000 + c.id * 0.37;
  const sep = 11 * k, mx = c.x, my = c.y;
  const fighting = end < 0, win = c.done === 'win', lose = c.done === 'lose';
  // the dust cloud they're scrapping in (behind them, so you can see who it is): puffs boiling round, with stars
  // and a fist or a paw poking out now and then; it clears as it ends
  const cloud = fighting ? 1 : Math.max(0, 1 - end * 2.2);
  if (cloud > 0.02) {
    g.save(); g.translate(mx, my - 9 * k); g.scale(k / 3, k / 3); g.globalAlpha = cloud;
    const puffs = 8, R = 32;
    const shapes = [];
    for (let i = 0; i < puffs; i++) {
      const a = i / puffs * Math.PI * 2 + t * 0.9, r = R * (0.55 + 0.12 * Math.sin(t * 6 + i * 2.1));
      shapes.push({ pts: I.ellPts(Math.cos(a) * r * 1.25, Math.sin(a) * r * 0.62, 22 + 5 * Math.sin(t * 7 + i), 19 + 4 * Math.cos(t * 6 + i), 10), id: c.id * 31 + i });
    }
    shapes.push({ pts: I.ellPts(0, 0, R * 1.05, R * 0.7, 14), id: c.id * 31 + 99 });
    I.union(g, shapes, PAPER, 2.6);
    // little action marks
    for (let i = 0; i < 4; i++) {
      const ph = (t * 1.6 + i / 4) % 1, a = i * 1.7 + Math.floor(t * 1.6 + i / 4) * 2.3, d = R * (0.9 + ph * 0.5);
      if (ph < 0.6) I.star4(g, Math.cos(a) * d * 1.2, Math.sin(a) * d * 0.7 - 6, 9 * Math.sin(ph / 0.6 * Math.PI), a, i % 2 ? '#f2c14e' : '#fff6d8', 1.6);
    }
    const jab = Math.sin(t * 9);   // a fist and a paw poking out now and then
    if (jab > 0.4) { I.oval(g, R * 0.95 + jab * 10, -4, 9, 8, sanitizeLook(c.lead.look) ? '#f8d3ad' : PAPER, c.id + 7, 2); }
    if (jab < -0.4) { I.oval(g, -R * 0.95 + jab * 10, 6, 10, 8, '#cfe0a8', c.id + 8, 2); }
    g.restore(); g.globalAlpha = 1;
  }
  // the leader: swinging away, or after a win a little hop of joy
  const showLead = fighting || win || end < 0.25;
  if (showLead) {
    const swing = fighting && Math.floor(t * 1.8) % 2 === 0, hop = win ? Math.abs(Math.sin(end * 7)) * 5 * Math.max(0, 1 - end / 1.6) : 0;
    const lx = win ? mx + sep * Math.max(0, 1 - end * 1.6) : mx + sep + Math.sin(t * 5) * 1.2 * k;
    const p = people.step(`brawl${c.id}`, sanitizeLook(c.lead.look), { job: c.lead.job, name: c.lead.name }, lx, my, win && end > 0.5 ? 0 : 3, swing, dt, now, win ? 'happy' : 'determined');
    if (lose || c.done === 'fled') g.globalAlpha = Math.max(0, 1 - end * 4);
    drawPerson(g, p, lx, my, hop * k, k, now);
    g.globalAlpha = 1;
  }
  // the creature: attacking, or after a win popping into ink
  const deadAt = win ? endedAt.get(c.id)! + 120 : 0;
  if (!win || end < 0.9) {
    const fx = fighting ? mx - sep + Math.sin(t * 4.3) * 1.5 * k : mx - sep * (lose ? 0.4 : 1);
    g.save(); if (k !== 1) { g.translate(fx, my); g.scale(k, k); g.translate(-fx, -my); }
    drawMob(g, { kind: c.foe, x: fx, y: my, dir: 1, moving: fighting, attackK: fighting ? Math.max(0, Math.sin(t * 3.6)) : 0, hp: win ? 0 : 1, hurtAt: fighting && Math.sin(t * 3.6 + 2) > 0.92 ? now : 0, deadAt, id: c.id }, now);
    g.restore();
  }
  if (cloud > 0.02) {   // dust kicked up round their feet, in front
    g.save(); g.translate(mx, my); g.scale(k / 3, k / 3); g.globalAlpha = cloud * 0.95;
    for (let i = 0; i < 5; i++) { const ph = (t * 1.3 + i / 5) % 1, x = (i - 2) * 16 + Math.sin(t * 3 + i) * 4; I.oval(g, x, 2 - ph * 6, 9 + ph * 6, 6 + ph * 4, PAPER, c.id * 13 + i, 1.8, 10); }
    g.restore(); g.globalAlpha = 1;
  }
  // a little card over it all: who's fighting what
  if (k >= 0.8) {
    const md = mobDef(c.foe), more = (c.count ?? 1) > 1 ? ` +${c.count - 1}` : '', foes = (c.foes ?? 1) > 1 ? ` ×${c.foes}` : '';
    const text = fighting ? `${c.lead.name}${more} vs ${md?.name ?? 'a creature'}${foes}` : win ? `${c.lead.name} won!` : lose ? `${md?.name ?? 'The creature'} won…` : `${c.lead.name} got away`;
    placeTag(g, mx, my - 34 * k, text, fighting ? (c.open ? 'help wanted' : 'battling') : null, !fighting, fighting && c.open);
  }
}
